import React from "react";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, ExternalLink } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { STORE } from "@/lib/store";
import { useSEO } from "@/lib/seo";
import { getProducts } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ProductMedia from "@/components/shop/ProductMedia";

export default function Cart() {
  const { items, updateQty, removeItem, count, clear, hydrateImages } = useCart();
  const subtotal = items.reduce((s, i) => s + (i.price != null ? Number(i.price) * i.qty : 0), 0);
  const { data: productData } = useQuery({ queryKey: ["products"], queryFn: () => getProducts() });

  // Hydrate cart items with fresh images/prices from the live catalog so older
  // entries (added before we started persisting images) still render correctly.
  useEffect(() => {
    if (!productData?.products?.length || !items.length) return;
    const bySlug = Object.fromEntries(productData.products.map((p) => [p.slug, p]));
    const patch = {};
    items.forEach((it) => {
      const p = bySlug[it.slug];
      if (!p) return;
      const next = {};
      if (!it.image && p.image) next.image = p.image;
      if ((it.price == null) && p.price != null) next.price = p.price;
      if (Object.keys(next).length) patch[it.slug] = next;
    });
    if (Object.keys(patch).length) hydrateImages(patch);
  }, [productData, items, hydrateImages]);

  useSEO({
    title: "Your Cart | Renova Peptide Co.",
    description: "Review your selected research materials before completing checkout on our secure store.",
    path: "/cart",
    noindex: true,
  });

  return (
    <>
      <PageHeader
        overline="Cart"
        title="Your Selection"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Cart", path: "/cart" }]}
      />
      <section className="container-rnv py-16 md:py-20">
        {items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#1E2A44]/15 rounded-sm" data-testid="cart-empty">
            <ShoppingBag size={40} className="mx-auto text-[#C8A96A]" strokeWidth={1.4} />
            <p className="font-serif text-2xl text-[#1E2A44] mt-5">Your cart is empty</p>
            <p className="mt-2 text-sm text-[#85827D]">Explore our research collections to get started.</p>
            <Link to="/shop" className="btn-navy mt-6">Browse Products</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#85827D]" data-testid="cart-count-label">{count} item{count !== 1 ? "s" : ""}</p>
                <button onClick={clear} className="text-sm text-[#85827D] hover:text-[#1E2A44] inline-flex items-center gap-1.5" data-testid="cart-clear">
                  <Trash2 size={14} /> Clear cart
                </button>
              </div>
              <ul className="divide-y divide-[#1E2A44]/10 border-y border-[#1E2A44]/10">
                {items.map((it) => (
                  <li key={it.slug} className="flex items-center gap-5 py-6" data-testid={`cart-item-${it.slug}`}>
                    <ProductMedia product={it} className="w-20 h-20 shrink-0 rounded-sm bg-[#F7F4EF]" imgClass="!p-1" />
                    <div className="flex-1 min-w-0">
                      <a href={it.store_url} className="font-serif text-lg text-[#1E2A44] hover:text-[#111C32]">{it.name}</a>
                      <p className="text-sm text-[#85827D]">{it.presentation}</p>
                      {it.price != null && (
                        <p className="text-sm font-semibold text-[#1E2A44] mt-0.5">${Number(it.price).toFixed(2)}</p>
                      )}
                    </div>
                    <div className="inline-flex items-center border border-[#1E2A44]/15 rounded-sm">
                      <button onClick={() => updateQty(it.slug, it.qty - 1)} aria-label="Decrease" className="p-2.5 text-[#1E2A44] hover:text-[#C8A96A]" data-testid={`cart-minus-${it.slug}`}><Minus size={14} /></button>
                      <span className="w-10 text-center text-sm" data-testid={`cart-qty-${it.slug}`}>{it.qty}</span>
                      <button onClick={() => updateQty(it.slug, it.qty + 1)} aria-label="Increase" className="p-2.5 text-[#1E2A44] hover:text-[#C8A96A]" data-testid={`cart-plus-${it.slug}`}><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeItem(it.slug)} aria-label={`Remove ${it.name}`} className="p-2 text-[#85827D] hover:text-[#1E2A44]" data-testid={`cart-remove-${it.slug}`}><Trash2 size={16} /></button>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-4">
              <div className="bg-white border border-[#1E2A44]/10 rounded-sm p-8 lg:sticky lg:top-28">
                <h2 className="font-serif text-2xl text-[#1E2A44]">Order Summary</h2>
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex justify-between text-[#85827D]"><span>Items</span><span>{count}</span></div>
                  {subtotal > 0 && (
                    <div className="flex justify-between text-[#262E3D] font-medium" data-testid="cart-subtotal"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between text-[#85827D]"><span>Shipping &amp; taxes</span><span>Calculated at checkout</span></div>
                </div>
                <div className="gold-rule my-6" />
                <p className="text-xs text-[#85827D] leading-relaxed mb-6">
                  Live pricing, discount codes, taxes, and shipping are applied securely at
                  checkout on our connected store.
                </p>
                <a href={STORE.cart} className="btn-navy w-full justify-center" data-testid="cart-checkout">
                  Proceed to Secure Checkout <ArrowRight size={16} />
                </a>
                <Link to="/shop" className="btn-gold w-full justify-center mt-3">Continue Shopping</Link>
                <p className="mt-5 text-[11px] text-[#85827D] text-center">
                  You will complete checkout on our Shift4Shop store, where each item can be
                  confirmed with live pricing.
                </p>
                <ul className="mt-5 space-y-2">
                  {items.map((it) => (
                    <li key={it.slug}>
                      <a href={it.store_url} className="text-xs text-[#1E2A44] inline-flex items-center gap-1 hover:text-[#C8A96A]">
                        {it.name} <ExternalLink size={11} className="text-[#C8A96A]" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        )}
      </section>
    </>
  );
}
